import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Moon, Sun, Menu, X } from "lucide-react";

export default function Navbar({ theme, toggleTheme }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const LINKS = [
    { to: "/basho", label: "Life of Bashō", testid: "nav-basho" },
    { to: "/daily", label: "Daily Haiku", testid: "nav-daily" },
    { to: "/rooms", label: "Renga Rooms", testid: "nav-rooms" },
    { to: "/kimono", label: "Kimono Room", testid: "nav-kimono" },
    { to: "/profile", label: "My Verses", testid: "nav-profile" },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="hanko text-primary text-2xl leading-none">俳</span>
          <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tight">
            Haiku House
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className="text-sm tracking-wide text-foreground/70 hover:text-foreground transition-colors ink-underline pb-0.5"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="p-2.5 hover:bg-muted transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* desktop auth */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="hidden lg:inline text-sm text-muted-foreground">{user.name}</span>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="text-sm border border-border px-4 py-1.5 hover:border-primary hover:text-primary transition-colors"
                >
                  Leave
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login"
                  className="text-sm text-foreground/70 hover:text-foreground transition-colors">
                  Enter
                </Link>
                <Link to="/register" data-testid="nav-register"
                  className="text-sm bg-primary text-primary-foreground px-4 py-1.5 hover:-translate-y-0.5 transition-transform">
                  Join
                </Link>
              </>
            )}
          </div>

          {/* mobile hamburger */}
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="md:hidden p-2.5 -mr-2 hover:bg-muted transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* mobile menu panel */}
      {open && (
        <nav
          data-testid="nav-mobile-menu"
          className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl"
        >
          <div className="px-6 py-4 flex flex-col divide-y divide-border">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className="py-3.5 text-base tracking-wide text-foreground/80 hover:text-primary transition-colors"
              >
                {l.label}
              </Link>
            ))}

            <div className="pt-4">
              {user ? (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-muted-foreground truncate">{user.name}</span>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-button-mobile"
                    className="text-sm border border-border px-5 py-2 hover:border-primary hover:text-primary transition-colors shrink-0"
                  >
                    Leave
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" data-testid="nav-login-mobile"
                    className="flex-1 text-center text-sm border border-border px-4 py-2.5 hover:border-primary transition-colors">
                    Enter
                  </Link>
                  <Link to="/register" data-testid="nav-register-mobile"
                    className="flex-1 text-center text-sm bg-primary text-primary-foreground px-4 py-2.5 hover:opacity-90 transition-opacity">
                    Join
                  </Link>
                </div>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
